import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  getUniquePropertyValues<T>(arr: T[], property: keyof T): T[keyof T][] {
    const uniqueValues = new Set<T[keyof T]>();
    return arr.reduce((acc: T[keyof T][], obj) => {
      if (!uniqueValues.has(obj[property])) {
        uniqueValues.add(obj[property]);
        acc.push(obj[property]);
      }
      return acc;
    }, []);
  }

  /**
   * Toggles a string value within an array of strings.
   * If the value is present, it is removed.
   * If the value is not present, it is added.
   * Returns a new array, leaving the original array immutable.
   *
   * @param arr The original array of strings.
   * @param value The string value to toggle.
   * @returns A new array with the toggled value.
   */
  toggleStringInArray = (arr: string[], value: string): string[] => {
    // Check if the value already exists in the array
    if (arr.includes(value)) {
      // If it exists, filter it out (remove it)
      return arr.filter((item) => item !== value);
    } else {
      // If it does not exist, add it to the array using the spread operator
      return [...arr, value];
    }
  };

  /**
   * Converts a Record<string, T> to an array of T using Object.values().
   * @param record The input record.
   * @returns An array of type T.
   */
  recordToArrayValues<T>(record: Record<string, T>): T[] {
    // Object.values() returns an array of the property values.
    return Object.values(record);
  }

  /**
   * Performs a full outer join on two arrays based on a common 'id' property.
   * Returns all objects present in either input array.
   * Objects with the same 'id' are merged (arr2 props override arr1 props).
   * @param arr1 The first array (used to build the base map).
   * @param arr2 The second array (used to find matches).
   * @returns A new array of merged objects present in either input array.
   */
  joinArraysById<T extends { id: number }, U extends { id: number }>(
    arr1: T[],
    arr2: U[],
  ): (T & U)[] {
    const map = new Map<number, T & U>();
    arr1.forEach((obj) => map.set(obj.id, { ...obj } as T & U));
    arr2.forEach((obj) => {
      const existing = map.get(obj.id);
      map.set(
        obj.id,
        existing ? { ...existing, ...obj } : ({ ...obj } as T & U),
      );
    });
    return Array.from(map.values()).sort((a, b) => a.id - b.id);
  }

  /**
   * Performs an "inner join" on two arrays based on a common 'id' property.
   * Returns only objects present in both input arrays.
   *
   * @param arr1 The first array (used to build the base map).
   * @param arr2 The second array (used to find matches).
   * @returns A new array of merged objects present in both inputs.
   */
  innerJoinArraysById<T extends { id: number }, U extends { id: number }>(
    arr1: T[],
    arr2: U[],
  ): (T & U)[] {
    const map1 = new Map<number, T>();
    const result: (T & U)[] = [];

    // 1. Build a lookup map from the first array for efficient O(1) lookups
    arr1.forEach((obj) => {
      map1.set(obj.id, obj);
    });

    // 2. Iterate through the second array
    arr2.forEach((obj2) => {
      const matchingObj1 = map1.get(obj2.id);

      // 3. If a match is found in both arrays, merge them and add to the result
      if (matchingObj1) {
        // Merge properties (arr2 props override arr1 props)
        result.push({ ...matchingObj1, ...obj2 });
      }
      // If no match, do nothing (this is the core difference from the full outer join)
    });

    // Optionally sort the final array
    return result.sort((a, b) => a.id - b.id);
  }

  /**
   * Filters the first array (T[]) to only include elements where the 'id'
   * exists in the second array (U[]). Returns T[] (left semi-join).
   *
   * @param primaryArray The array to filter (T[]).
   * @param trimmingArray The array used for ID lookup (U[]).
   * @returns An array containing only the matching elements from the primaryArray.
   */
  trimArrayByMatch<T extends { id: number }, U extends { id: number }>(
    primaryArray: T[],
    trimmingArray: U[],
  ): T[] {
    // 1. Create a Set of all IDs present in the trimming array for quick lookup
    const trimmingIds = new Set(trimmingArray.map((item) => item.id));

    // 2. Use filter() on the primary array
    const filteredArray = primaryArray.filter((item) => {
      // 3. Keep the item if its ID is present in the trimming set
      return trimmingIds.has(item.id);
    });

    // Optionally sort the final array
    return filteredArray.sort((a, b) => a.id - b.id);
  }
}
